import { dateUtils } from '@festina-life/core'
import { useResizeObserver } from '@festina-life/ui/hooks'
import { useMemo, useRef, useState } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { useParameterDefinitionId } from '../../hooks/useParameterDefinitionId'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import {
  ARROW_LINE_WIDTH,
  DATE_HEADING_HEIGHT,
} from '../versionedTimeline/constants'
import { getVersionDateBoundaries } from '../versionedTimeline/helpers'
import { TimelineScaleControls } from '../versionedTimeline/TimelineScaleControls'
import { VersionedArrowsGroup } from '../versionedTimeline/VersionedArrowsGroup'
import { ParameterDefinitionVersionsLine } from './ParameterDefinitionVersionsLine'

export const ParameterDefinitionTimelineDiagram = () => {
  const divRef = useRef<HTMLDivElement>(null)
  const divObserver = useResizeObserver(divRef)
  const { viewAt } = useConfigurationContext()
  const parameterDefinitionId = useParameterDefinitionId()
  const { applicationParameterDefinitions } =
    useApplicationViewOperationsContext()

  const definitionsForId = applicationParameterDefinitions?.get(
    parameterDefinitionId,
  )

  const allVersions = useMemo(
    () => definitionsForId?.operations ?? [],
    [definitionsForId?.operations],
  )

  const [currentScale, setCurrentScale] = useState(1)

  const { earliestDate: earliestVersionDate, latestDate: latestVersionDate } =
    useMemo(
      () =>
        getVersionDateBoundaries({
          items: allVersions,
          getFromDate: (version) => version.internalOperation.entity?.fromDate,
          defaultDate: dateUtils.dateAsServerStr(viewAt),
        }),
      [allVersions, viewAt],
    )

  return (
    <div ref={divRef}>
      <div className="group relative grid grid-cols-[auto_1fr] overflow-hidden">
        <TimelineScaleControls step={0.5} onChange={setCurrentScale} />
        <div className="overflow-auto">
          <VersionedArrowsGroup
            height={DATE_HEADING_HEIGHT + ARROW_LINE_WIDTH}
            scale={currentScale}
            parentWidth={divObserver?.width ?? 0}
            earliestVersionDate={earliestVersionDate}
            latestVersionDate={latestVersionDate}
            nodeHeight={0} // single node, so height isn't needed for positioning
            versions={allVersions}
            paddingTop={0}
          >
            {ParameterDefinitionVersionsLine}
          </VersionedArrowsGroup>
        </div>
      </div>
    </div>
  )
}
